import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { FileQuestion, Wrench, Car, Phone, Mail, MapPin, X, Calendar, DollarSign, Gauge, Receipt, ExternalLink, Eye, Trash2 } from "lucide-react";

const TIME_FILTERS = [
  { key: "today",      label: "Today" },
  { key: "yesterday",  label: "Yesterday" },
  { key: "this_week",  label: "This Week" },
  { key: "last_week",  label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "all_time",   label: "All Time" },
];

const REASON_META = {
  quoted_accepted:      { label: "Quoted & Accepted",   color: "#15803d" },
  already_bought:       { label: "Already Bought",      color: "#15803d" },
  no_longer_interested: { label: "No Longer Interested",color: "#92400e" },
  duplicate_request:    { label: "Duplicate",           color: "#92400e" },
  part_not_available:   { label: "Part Not Available",  color: "#92400e" },
  spam:                 { label: "Spam",                color: "#991b1b" },
  other:                { label: "Other",                color: "#64748b" },
};

// Row colouring: yellow = awaiting action, green = quoted or a successful
// outcome, red = closed with no value (spam, duplicate, etc.)
const GOOD_REASONS = new Set(["quoted_accepted", "already_bought"]);
function rowTint(q) {
  if (q.status === "new") return { background: "#fffbeb" };
  if (q.status === "quoted") return { background: "#f0fdf4" };
  if (q.status === "closed") {
    if (GOOD_REASONS.has(q.close_reason)) return { background: "#f0fdf4" };
    if (q.close_reason) return { background: "#fef2f2" };
  }
  return {};
}

/* ── Detail modal — full customer info + untruncated notes ─────── */
function QuoteDetailModal({ quote, onClose, onMark, onSaveNotes, onDelete }) {
  const isVehicle = quote.quote_type === "vehicle";
  const navigate = useNavigate();
  const [notesDraft, setNotesDraft] = useState(quote.admin_notes || "");
  const [closeReason, setCloseReason] = useState(quote.close_reason || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const STATUS_STYLE = {
    new:    { bg:"#fef3c7", color:"#92400e" },
    quoted: { bg:"#dcfce7", color:"#15803d" },
    closed: { bg:"#f1f5f9", color:"#475569" },
  };
  const style = STATUS_STYLE[quote.status] || STATUS_STYLE.closed;

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await onSaveNotes(quote.id, notesDraft);
      toast.success("Note saved");
    } finally { setSavingNotes(false); }
  };

  const createQuote = () => {
    navigate("/admin/price-quotes", {
      state: {
        prefill: {
          request_id: quote.id,
          customer_name: quote.name,
          customer_email: quote.email,
          customer_phone: quote.phone || "",
          items: [{
            source: "loose",
            name: isVehicle ? "Vehicle" : (quote.part_needed || "Part"),
            vehicle_make: quote.vehicle_make || "",
            vehicle_model: quote.vehicle_model || "",
            vehicle_year: quote.vehicle_year || "",
            stock_no: "",
            price: 0,
            notes: quote.notes || "",
          }],
        },
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background:"rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="quote-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
        style={{ border:"1px solid #dde3ef" }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div className="p-5 flex items-start justify-between sticky top-0 bg-white rounded-t-2xl" style={{ borderBottom:"1px solid #eef2f8" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>
              {isVehicle ? "Vehicle / Sell Request" : "Part Quote Request"}
            </div>
            <div className="font-heading text-xl font-bold" style={{ color:"#0f172a" }}>{quote.quote_number}</div>
            <div className="flex items-center gap-1 text-[11px] mt-1" style={{ color:"#94a3b8" }}>
              <Calendar className="h-3 w-3" /> {new Date(quote.created_at).toLocaleString("en-AU")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg" style={style}>
              {quote.status}
            </span>
            <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-lg" style={{ color:"#94a3b8" }}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* Customer */}
          <div className="p-3 rounded-xl" style={{ background:"#f8fafc", border:"1px solid #eef2f8" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>Customer</div>
            <div className="font-semibold text-[14px]" style={{ color:"#0f172a" }}>{quote.name}</div>
            <a href={`mailto:${quote.email}`} className="flex items-center gap-1.5 text-[12px] mt-1 hover:underline" style={{ color:"#1e5bb8" }}>
              <Mail className="h-3 w-3" /> {quote.email}
            </a>
            {quote.phone && (
              <a href={`tel:${quote.phone}`} className="flex items-center gap-1.5 text-[12px] mt-1 hover:underline" style={{ color:"#1e5bb8" }}>
                <Phone className="h-3 w-3" /> {quote.phone}
              </a>
            )}
          </div>

          {/* Vehicle */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>Vehicle</div>
            <div className="font-medium text-[14px]" style={{ color:"#0f172a" }}>
              {[quote.vehicle_year, quote.vehicle_make, quote.vehicle_model].filter(Boolean).join(" ") || "Not specified"}
            </div>
            {isVehicle && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[12px]" style={{ color:"#64748b" }}>
                {quote.vehicle_condition && <span>Condition: <b style={{ color:"#0f172a" }}>{quote.vehicle_condition}</b></span>}
                {quote.vehicle_km && <span className="flex items-center gap-1"><Gauge className="h-3 w-3" />{quote.vehicle_km} km</span>}
                {quote.vehicle_location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{quote.vehicle_location}</span>}
                {quote.asking_price && <span className="flex items-center gap-1" style={{ color:"#16a34a" }}><DollarSign className="h-3 w-3" />{quote.asking_price}</span>}
              </div>
            )}
          </div>

          {/* Part needed (part requests only) */}
          {!isVehicle && quote.part_needed && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>Part Needed</div>
              <div className="font-medium text-[14px] whitespace-pre-wrap" style={{ color:"#0f172a" }}>{quote.part_needed}</div>
            </div>
          )}

          {/* Full notes — never truncated here */}
          {quote.notes && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>Customer's Notes</div>
              <div className="text-[13px] whitespace-pre-wrap leading-relaxed p-3 rounded-xl" style={{ color:"#374151", background:"#f8fafc", border:"1px solid #eef2f8" }}>
                {quote.notes}
              </div>
            </div>
          )}

          {/* Linked quote — what was actually quoted, if a formal quote was sent */}
          {quote.linked_quote_id && (
            <div className="p-3 rounded-xl" style={{ background:"#f0fdf4", border:"1px solid #bbf7d0" }}>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#15803d" }}>Quoted</div>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[14px]" style={{ color:"#0f172a" }}>{quote.linked_quote_number}</div>
                <div className="font-mono font-bold text-[15px]" style={{ color:"#15803d" }}>${(quote.quoted_price || 0).toFixed(2)}</div>
              </div>
              {quote.quoted_at && (
                <div className="text-[11px] mt-1" style={{ color:"#94a3b8" }}>{new Date(quote.quoted_at).toLocaleString("en-AU")}</div>
              )}
            </div>
          )}

          {/* Admin notes — staff-only reason/comment, visible here regardless of status */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>Admin Notes (internal)</div>
            <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)}
              rows={3} placeholder="Why this was closed, what was discussed on the phone, etc."
              className="w-full px-3 py-2 rounded-xl text-[13px] outline-none resize-none"
              style={{ border:"1px solid #dde3ef", color:"#0f172a" }} />
            <div className="flex justify-end mt-1.5">
              <button onClick={saveNotes} disabled={savingNotes}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg"
                style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
                {savingNotes ? "Saving…" : "Save Note"}
              </button>
            </div>
            {quote.admin_notes_by && (
              <div className="text-[10px] mt-1" style={{ color:"#94a3b8" }}>
                Last updated by {quote.admin_notes_by}{quote.admin_notes_at ? ` · ${new Date(quote.admin_notes_at).toLocaleString("en-AU")}` : ""}
              </div>
            )}
          </div>
        </div>

        {/* Reason selector — shown whenever the request could be closed
            (from new, e.g. spam/duplicate with no quote ever given, or
            from quoted, e.g. accepted/already bought) */}
        {(quote.status === "new" || quote.status === "quoted") && (
          <div className="px-5 pb-2">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>
              Reason (required to close)
            </div>
            <select value={closeReason} onChange={e => setCloseReason(e.target.value)}
              className="w-full h-10 px-3 rounded-xl text-[13px] outline-none"
              style={{ border:"1px solid #dde3ef", color:"#0f172a" }}
              data-testid="quote-close-reason">
              <option value="">Select a reason…</option>
              {Object.entries(REASON_META).map(([key, meta]) => (
                <option key={key} value={key}>{meta.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Actions */}
        <div className="p-5 space-y-2 sticky bottom-0 bg-white rounded-b-2xl" style={{ borderTop:"1px solid #eef2f8" }}>
          <button onClick={createQuote}
            className="w-full h-10 rounded-xl flex items-center justify-center gap-2 text-white text-[13px] font-bold uppercase tracking-wide"
            style={{ background:"#1e5bb8" }} data-testid="request-create-quote">
            <Receipt className="h-4 w-4" /> Create Quote
          </button>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
              style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
              Close
            </button>
            {quote.status === "new" && (
              <button onClick={() => onMark(quote.id, "quoted", notesDraft, closeReason)}
                className="flex-1 h-10 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide"
                style={{ background:"#16a34a" }}>
                {isVehicle ? "Offer Made" : "Mark Quoted"}
              </button>
            )}
            {(quote.status === "new" || quote.status === "quoted") && (
              <button onClick={() => {
                  if (!closeReason) { toast.error("Pick a reason first"); return; }
                  onMark(quote.id, "closed", notesDraft, closeReason);
                }}
                className="flex-1 h-10 rounded-xl text-[13px] font-bold uppercase tracking-wide"
                style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
                Close Out
              </button>
            )}
          </div>
          {quote.status === "new" && (
            <button onClick={() => onDelete(quote.id)}
              className="w-full h-9 rounded-xl flex items-center justify-center gap-2 text-[12px] font-semibold"
              style={{ background:"#fef2f2", color:"#991b1b" }}
              data-testid="quote-delete-modal-btn">
              <Trash2 className="h-3.5 w-3.5" /> Delete Request
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminQuotes() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("part"); // "part" | "vehicle"
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [rangeKey, setRangeKey] = useState("today");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/quotes", { params: { range_key: rangeKey } });
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load quotes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [rangeKey]);

  const mark = async (id, status, adminNotes, closeReason) => {
    await adminApi.put(`/admin/quotes/${id}/status`, { status, admin_notes: adminNotes, close_reason: closeReason });
    toast.success(`Quote marked ${status}`);
    setSelectedQuote(null);
    load();
  };

  const saveNotes = async (id, adminNotes) => {
    await adminApi.put(`/admin/quotes/${id}/status`, { status: selectedQuote.status, admin_notes: adminNotes });
    load();
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Delete this quote request? This can't be undone.")) return;
    try {
      await adminApi.delete(`/admin/quotes/${id}`);
      toast.success("Request deleted");
      setSelectedQuote(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete this request");
    }
  };

  // Split by quote_type — items without quote_type default to "part"
  const partItems    = items.filter((q) => !q.quote_type || q.quote_type === "part");
  const vehicleItems = items.filter((q) => q.quote_type === "vehicle");

  const partPending    = partItems.filter((q)    => q.status === "new").length;
  const vehiclePending = vehicleItems.filter((q) => q.status === "new").length;

  const displayed = tab === "part" ? partItems : vehicleItems;

  const STATUS_STYLE = {
    new:    "bg-yellow-500/20 text-yellow-400",
    quoted: "bg-green-500/20 text-green-400",
    closed: "bg-white/5 text-blue-300",
  };

  return (
    <div className="space-y-6" data-testid="admin-quotes-page">

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase text-white">Quote Requests</h1>
          <p className="text-sm text-blue-200 mt-1">
            {partPending} part pending · {vehiclePending} vehicle pending
          </p>
        </div>
        <Button onClick={load} variant="outline"
          className="border-white/20 text-blue-200 hover:text-white hover:border-white/40 rounded-sm text-xs uppercase tracking-wide">
          Refresh
        </Button>
      </div>

      {/* ── Time filter ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 flex-wrap bg-white/5 border border-white/10 rounded-sm p-1" data-testid="quote-requests-time-filter">
        {TIME_FILTERS.map(({ key, label }) => (
          <button key={key} onClick={() => setRangeKey(key)}
            className="px-3 py-1.5 rounded-sm text-[11px] font-semibold uppercase tracking-wide transition-colors"
            style={{ background: rangeKey === key ? "#e31e24" : "transparent", color: rangeKey === key ? "#fff" : "#93c5fd" }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab toggle ───────────────────────────────────────────────── */}
      <div className="inline-flex bg-white/5 border border-white/10 rounded-sm p-1 gap-1">
        <button
          onClick={() => setTab("part")}
          className={`flex items-center gap-2 px-5 py-2 rounded-sm text-[13px] font-heading font-semibold uppercase tracking-wide transition-all ${
            tab === "part"
              ? "bg-[#e31e24] text-white"
              : "text-blue-300 hover:text-white"
          }`}>
          <Wrench className="h-3.5 w-3.5" />
          Part Requests
          {partPending > 0 && (
            <span className="ml-1 w-5 h-5 rounded-full bg-white/20 text-white text-[10px] font-bold flex items-center justify-center">
              {partPending}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("vehicle")}
          className={`flex items-center gap-2 px-5 py-2 rounded-sm text-[13px] font-heading font-semibold uppercase tracking-wide transition-all ${
            tab === "vehicle"
              ? "bg-[#e31e24] text-white"
              : "text-blue-300 hover:text-white"
          }`}>
          <Car className="h-3.5 w-3.5" />
          Vehicle / Sell Requests
          {vehiclePending > 0 && (
            <span className="ml-1 w-5 h-5 rounded-full bg-white/20 text-white text-[10px] font-bold flex items-center justify-center">
              {vehiclePending}
            </span>
          )}
        </button>
      </div>

      {/* ── Content ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="admin-card rounded-sm p-12 text-center">
          <div className="text-blue-200 text-xs font-mono uppercase tracking-widest animate-pulse">Loading…</div>
        </div>
      ) : displayed.length === 0 ? (
        <div className="admin-card rounded-sm p-12 text-center">
          {tab === "part" ? <Wrench className="h-10 w-10 mx-auto text-blue-300/40 mb-3" /> : <Car className="h-10 w-10 mx-auto text-blue-300/40 mb-3" />}
          <div className="text-blue-200 text-xs font-mono uppercase tracking-widest">
            No {tab === "part" ? "part" : "vehicle"} requests yet
          </div>
        </div>
      ) : tab === "part" ? (

        /* ── Part requests table ─────────────────────────────────── */
        <div className="admin-card rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-[0.2em] text-blue-200 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Quote #</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Part Needed</th>
                <th className="text-left px-4 py-3">Admin Notes</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {displayed.map((q) => {
                const tint = rowTint(q);
                const dark = Object.keys(tint).length > 0;
                const reasonMeta = q.close_reason ? REASON_META[q.close_reason] : null;
                return (
                <tr key={q.id} className="hover:brightness-95 cursor-pointer transition-all" style={tint} onClick={() => setSelectedQuote(q)} data-testid={`quote-row-${q.quote_number}`}>
                  <td className="px-4 py-3">
                    <div className="font-heading font-bold" style={{ color: dark ? "#0f172a" : "#fff" }}>{q.quote_number}</div>
                    <div className="text-[10px] font-mono" style={{ color: dark ? "#64748b" : "#93c5fd" }}>{new Date(q.created_at).toLocaleDateString("en-AU")}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium" style={{ color: dark ? "#0f172a" : "#fff" }}>{q.name}</div>
                    <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: dark ? "#64748b" : "#93c5fd" }}>
                      <Mail className="h-3 w-3" /> {q.email}
                    </div>
                    {q.phone && (
                      <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: dark ? "#64748b" : "#93c5fd" }}>
                        <Phone className="h-3 w-3" /> {q.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[13px]" style={{ color: dark ? "#334155" : "#dbeafe" }}>
                    {[q.vehicle_year, q.vehicle_make, q.vehicle_model].filter(Boolean).join(" ") || <span className="italic opacity-50">Not specified</span>}
                  </td>
                  <td className="px-4 py-3 max-w-[180px]">
                    <div className="text-sm font-medium truncate" style={{ color: dark ? "#0f172a" : "#fff" }}>{q.part_needed}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[200px]">
                    {reasonMeta && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md inline-block mb-1" style={{ background: "#fff", color: reasonMeta.color, border: `1px solid ${reasonMeta.color}33` }}>
                        {reasonMeta.label}
                      </span>
                    )}
                    {q.admin_notes && (
                      <div className="text-[11px] truncate" style={{ color: dark ? "#475569" : "#93c5fd" }} title={q.admin_notes}>{q.admin_notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm ${STATUS_STYLE[q.status] || STATUS_STYLE.closed}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e=>e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setSelectedQuote(q)}
                        className="h-7 px-2.5 rounded-md flex items-center gap-1 text-[11px] font-semibold"
                        style={{ background: dark ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)", color: dark ? "#0f172a" : "#fff" }}
                        data-testid={`quote-view-${q.quote_number}`}>
                        <Eye className="h-3 w-3" /> View
                      </button>
                      {q.status === "new" && (
                        <button onClick={() => deleteRequest(q.id)}
                          className="h-7 w-7 flex items-center justify-center rounded-md"
                          style={{ background: dark ? "rgba(153,27,27,0.1)" : "rgba(255,255,255,0.08)", color: "#e31e24" }}
                          data-testid={`quote-delete-${q.quote_number}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      ) : (

        /* ── Vehicle / sell requests table ───────────────────────── */
        <div className="admin-card rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-[0.2em] text-blue-200 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Ref #</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Condition / KM</th>
                <th className="text-left px-4 py-3">Location / Price</th>
                <th className="text-left px-4 py-3">Admin Notes</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {displayed.map((q) => {
                const tint = rowTint(q);
                const dark = Object.keys(tint).length > 0;
                const reasonMeta = q.close_reason ? REASON_META[q.close_reason] : null;
                return (
                <tr key={q.id} className="hover:brightness-95 cursor-pointer transition-all" style={tint} onClick={() => setSelectedQuote(q)}>
                  <td className="px-4 py-3">
                    <div className="font-heading font-bold" style={{ color: dark ? "#0f172a" : "#fff" }}>{q.quote_number}</div>
                    <div className="text-[10px] font-mono" style={{ color: dark ? "#64748b" : "#93c5fd" }}>{new Date(q.created_at).toLocaleDateString("en-AU")}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium" style={{ color: dark ? "#0f172a" : "#fff" }}>{q.name}</div>
                    <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: dark ? "#64748b" : "#93c5fd" }}>
                      <Mail className="h-3 w-3" /> {q.email}
                    </div>
                    {q.phone && (
                      <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: dark ? "#64748b" : "#93c5fd" }}>
                        <Phone className="h-3 w-3" /> {q.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium" style={{ color: dark ? "#0f172a" : "#fff" }}>
                      {[q.vehicle_year, q.vehicle_make, q.vehicle_model].filter(Boolean).join(" ") || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {q.vehicle_condition ? (
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm ${
                        q.vehicle_condition === "Excellent" ? "bg-green-500/20 text-green-300"
                          : q.vehicle_condition === "Good" ? "bg-blue-500/20 text-blue-300"
                            : q.vehicle_condition === "Fair" ? "bg-yellow-500/20 text-yellow-300"
                              : "bg-red-500/20 text-red-300"
                      }`}>{q.vehicle_condition}</span>
                    ) : <span className="text-[11px] opacity-50" style={{ color: dark ? "#64748b" : "#93c5fd" }}>—</span>}
                    {q.vehicle_km && (
                      <div className="text-[11px] mt-1" style={{ color: dark ? "#64748b" : "#93c5fd" }}>{q.vehicle_km} km</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {q.vehicle_location && (
                      <div className="flex items-center gap-1 text-[12px]" style={{ color: dark ? "#334155" : "#dbeafe" }}>
                        <MapPin className="h-3 w-3 shrink-0" /> {q.vehicle_location}
                      </div>
                    )}
                    {q.asking_price && (
                      <div className="text-[12px] font-mono mt-1" style={{ color: "#16a34a" }}>{q.asking_price}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-[180px]">
                    {reasonMeta && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md inline-block mb-1" style={{ background: "#fff", color: reasonMeta.color, border: `1px solid ${reasonMeta.color}33` }}>
                        {reasonMeta.label}
                      </span>
                    )}
                    {q.admin_notes && (
                      <div className="text-[11px] truncate" style={{ color: dark ? "#475569" : "#93c5fd" }} title={q.admin_notes}>{q.admin_notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm ${STATUS_STYLE[q.status] || STATUS_STYLE.closed}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e=>e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setSelectedQuote(q)}
                        className="h-7 px-2.5 rounded-md flex items-center gap-1 text-[11px] font-semibold"
                        style={{ background: dark ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)", color: dark ? "#0f172a" : "#fff" }}>
                        <Eye className="h-3 w-3" /> View
                      </button>
                      {q.status === "new" && (
                        <button onClick={() => deleteRequest(q.id)}
                          className="h-7 w-7 flex items-center justify-center rounded-md"
                          style={{ background: dark ? "rgba(153,27,27,0.1)" : "rgba(255,255,255,0.08)", color: "#e31e24" }}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                      {q.phone && (
                        <a href={`tel:${q.phone}`} onClick={e => e.stopPropagation()}
                          className="h-7 w-7 flex items-center justify-center rounded-md"
                          style={{ background: dark ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)", color: dark ? "#0f172a" : "#fff" }}>
                          <Phone className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedQuote && (
        <QuoteDetailModal
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onMark={mark}
          onSaveNotes={saveNotes}
          onDelete={deleteRequest}
        />
      )}
    </div>
  );
}